import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

const CHANNELS = [
  { value: 'all', label: 'Todos', icon: '\u25CB' },
  { value: 'email', label: 'Email', icon: '\u2709' },
  { value: 'whatsapp', label: 'WhatsApp', icon: '\uD83D\uDCAC' },
  { value: 'chat', label: 'Chat', icon: '\uD83D\uDDE8' },
];

function AlcazarReporting() {
  const { user } = useAuth();
  const isEditor = user && ['superadmin', 'admin', 'revisor'].includes(user.role);

  const now = new Date();
  const today = now.toISOString().split('T')[0];

  const [from, setFrom] = useState(today);
  const [to, setTo] = useState(today);
  const [channel, setChannel] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [billing, setBilling] = useState(null);

  const fetchReport = useCallback(async () => {
    try {
      setLoading(true);
      const [reportRes, billingRes] = await Promise.all([
        api.get('/api/alcazar/reporting', { params: { from, to, channel } }),
        api.get('/api/alcazar/reporting/billing', { params: { from, to } }),
      ]);
      if (reportRes.data.success) setData(reportRes.data);
      if (billingRes.data.success) setBilling(billingRes.data);
      setError(null);
    } catch (err) {
      if (err.response?.status === 401) return;
      setError('Error al cargar reporting');
    } finally {
      setLoading(false);
    }
  }, [from, to, channel]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  const exportCSV = async () => {
    setExporting(true);
    try {
      const res = await api.get('/api/alcazar/reporting/export', {
        params: { from, to },
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `xia_report_${from}_${to}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch {
      alert('Error al exportar CSV');
    } finally {
      setExporting(false);
    }
  };

  const email = data?.email || {};
  const channels = data?.channels || {};
  const daily = data?.daily || [];
  const topics = data?.topics || [];
  const languages = data?.languages || [];
  const knowledge = data?.knowledge || {};

  const fmtMs = (ms) => {
    if (!ms && ms !== 0) return '-';
    if (ms < 1000) return ms + ' ms';
    return (ms / 1000).toFixed(1) + ' s';
  };

  const topicTotal = topics.reduce((s, t) => s + Number(t.count || 0), 0);
  const topicsWithPct = topics.map(t => ({
    ...t,
    count: Number(t.count),
    pct: topicTotal > 0 ? ((Number(t.count) / topicTotal) * 100).toFixed(1) : '0',
  }));

  const langTotal = languages.reduce((s, l) => s + Number(l.count || 0), 0);
  const langsWithPct = languages.map(l => ({
    ...l,
    count: Number(l.count),
    pct: langTotal > 0 ? ((Number(l.count) / langTotal) * 100).toFixed(1) : '0',
  }));

  const dailyNorm = daily.map(d => ({
    date: d.date,
    total: Number(d.total || 0),
    auto_resolved: Number(d.auto_resolved || 0),
    handoff: Number(d.handoff || 0),
    email: Number(d.email || 0),
    whatsapp: Number(d.whatsapp || 0),
  }));
  const maxDayCount = dailyNorm.length > 0 ? Math.max(...dailyNorm.map(d => d.total), 1) : 1;

  return (
    <div className="space-y-4">
      {/* Filters: dates + channel + export */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-semibold text-gray-700">Periodo:</span>
          <input type="date" value={from} onChange={e => setFrom(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          <span className="text-gray-400">&rarr;</span>
          <input type="date" value={to} onChange={e => setTo(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm" />
          <span className="text-gray-300 mx-1">|</span>
          {[
            { label: 'Hoy', fn: () => { setFrom(today); setTo(today); } },
            { label: '7d', fn: () => { setFrom(new Date(Date.now()-7*86400000).toISOString().split('T')[0]); setTo(today); } },
            { label: '30d', fn: () => { setFrom(new Date(Date.now()-30*86400000).toISOString().split('T')[0]); setTo(today); } },
            { label: 'Mes', fn: () => { setFrom(new Date(now.getFullYear(),now.getMonth(),1).toISOString().split('T')[0]); setTo(today); } },
          ].map(p => (
            <button key={p.label} onClick={p.fn}
              className="px-2 py-1 rounded text-xs font-medium bg-gray-100 text-gray-600 hover:bg-gray-200">
              {p.label}
            </button>
          ))}

          <span className="text-gray-300 mx-1">|</span>

          <span className="text-sm font-semibold text-gray-700">Canal:</span>
          {CHANNELS.map(c => (
            <button key={c.value} onClick={() => setChannel(c.value)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                channel === c.value
                  ? 'bg-purple-600 text-white'
                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}>
              {c.icon} {c.label}
            </button>
          ))}

          {isEditor && (
            <button onClick={exportCSV} disabled={exporting}
              className="px-4 py-1.5 bg-green-600 text-white rounded-lg text-sm hover:bg-green-700 disabled:opacity-50 ml-auto">
              {exporting ? 'Exportando...' : 'Exportar CSV'}
            </button>
          )}
        </div>
      </div>

      {loading ? (
        <div className="bg-white rounded-lg shadow p-8 text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="mt-3 text-gray-500">Cargando reporting...</p>
        </div>
      ) : error ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">{error}</div>
      ) : (
        <>
          {/* Channel summary cards */}
          {channel === 'all' && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-blue-500">
                <div className="text-xs text-gray-500 uppercase mb-1">Email</div>
                <div className="text-2xl font-bold text-blue-700">{channels.email?.total || 0}</div>
                <div className="text-xs text-gray-400 mt-1">{channels.email?.auto_resolved || 0} auto / {channels.email?.handoff || 0} handoff</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-green-500">
                <div className="text-xs text-gray-500 uppercase mb-1">WhatsApp</div>
                <div className="text-2xl font-bold text-green-700">{channels.whatsapp?.total || 0}</div>
                <div className="text-xs text-gray-400 mt-1">{channels.whatsapp?.auto_resolved || 0} auto / {channels.whatsapp?.handoff || 0} handoff</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-orange-500">
                <div className="text-xs text-gray-500 uppercase mb-1">Chat</div>
                <div className="text-2xl font-bold text-orange-700">{channels.chat?.total || 0}</div>
                <div className="text-xs text-gray-400 mt-1">{channels.chat?.auto_resolved || 0} auto / {channels.chat?.handoff || 0} handoff</div>
              </div>
              <div className="bg-white rounded-lg shadow p-4 border-l-4 border-purple-500">
                <div className="text-xs text-gray-500 uppercase mb-1">Total</div>
                <div className="text-2xl font-bold text-purple-700">{email.total || 0}</div>
                <div className="text-xs text-gray-400 mt-1">{email.auto_resolved || 0} auto / {email.handoff || 0} handoff</div>
              </div>
            </div>
          )}

          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <KpiCard label="Gestionados" value={email.total || 0} color="blue" subtitle={channel === 'all' ? 'Todos los canales' : channel} />
            <KpiCard label="Auto-resueltos" value={`${email.auto_resolved || 0} (${email.total > 0 ? Math.round((email.auto_resolved||0)*100/email.total) : 0}%)`} color="green" />
            <KpiCard label="Handoff" value={`${email.handoff || 0} (${email.total > 0 ? Math.round((email.handoff||0)*100/email.total) : 0}%)`} color="yellow" />
            <KpiCard label="Tiempo medio" value={fmtMs(email.avg_response_ms)} color="purple" />
            <KpiCard label="p50" value={fmtMs(email.p50_ms)} color="purple" />
            <KpiCard label="p95" value={fmtMs(email.p95_ms)} color="purple" />
          </div>

          {/* Knowledge summary */}
          {knowledge.total_topics > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <span>Base de conocimiento: <strong>{knowledge.total_topics}</strong> topics</span>
                <span>Idiomas: <strong>{knowledge.total_languages}</strong></span>
              </div>
            </div>
          )}

          {/* By Day */}
          {dailyNorm.length > 0 && (
            <div className="bg-white rounded-lg shadow p-4">
              <h3 className="text-sm font-semibold text-gray-700 mb-3">Interacciones por dia</h3>
              <div className="space-y-1.5">
                {dailyNorm.map(d => (
                  <div key={d.date} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-20 shrink-0">
                      {new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                    </span>
                    <div className="flex-1 bg-gray-100 rounded-full h-5 overflow-hidden flex">
                      {channel === 'all' && d.email > 0 && (
                        <div className="h-full flex items-center" title={`Email: ${d.email}`}
                          style={{ width: `${Math.max(2, (d.email / maxDayCount) * 100)}%`, background: '#3b82f6' }}>
                          <span className="text-xs text-white font-medium pl-1">{d.email}</span>
                        </div>
                      )}
                      {channel === 'all' && d.whatsapp > 0 && (
                        <div className="h-full flex items-center" title={`WhatsApp: ${d.whatsapp}`}
                          style={{ width: `${Math.max(2, (d.whatsapp / maxDayCount) * 100)}%`, background: '#22c55e' }}>
                          <span className="text-xs text-white font-medium pl-1">{d.whatsapp}</span>
                        </div>
                      )}
                      {channel !== 'all' && (
                        <div className="h-full rounded-full flex items-center"
                          style={{
                            width: `${Math.max(2, (d.total / maxDayCount) * 100)}%`,
                            background: channel === 'whatsapp' ? '#22c55e' : '#8b5cf6',
                          }}>
                          {d.total > 0 && <span className="text-xs text-white font-medium pl-2">{d.total}</span>}
                        </div>
                      )}
                    </div>
                    <span className="text-xs text-gray-400 w-24 text-right">
                      {d.auto_resolved} auto / {d.handoff} hoff
                    </span>
                  </div>
                ))}
              </div>
              {channel === 'all' && (
                <div className="flex gap-4 mt-2 text-xs text-gray-400">
                  <span><span className="inline-block w-3 h-3 rounded" style={{background:'#3b82f6'}}></span> Email</span>
                  <span><span className="inline-block w-3 h-3 rounded" style={{background:'#22c55e'}}></span> WhatsApp</span>
                </div>
              )}
            </div>
          )}

          {/* BILLING / FACTURACION */}
          {billing && (
            <div className="space-y-4">
              <div className="bg-white rounded-lg shadow p-4">
                <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  Facturacion
                  <span style={{ fontSize: '10px', backgroundColor: '#dcfce7', color: '#166534', padding: '2px 8px', borderRadius: '4px', fontWeight: 600 }}>FACTURABLE</span>
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-green-800">{billing.summary?.billable_total || 0}</div>
                    <div className="text-xs text-green-600 uppercase mt-1">Facturables</div>
                  </div>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-blue-800">{billing.summary?.xia_auto || 0}</div>
                    <div className="text-xs text-blue-600 uppercase mt-1">XIA Auto</div>
                  </div>
                  <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-cyan-800">{billing.summary?.xia_from_entradas || 0}</div>
                    <div className="text-xs text-cyan-600 uppercase mt-1">Via Entradas</div>
                  </div>
                  <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-orange-800">{billing.summary?.manual_human || 0}</div>
                    <div className="text-xs text-orange-600 uppercase mt-1">Manual</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-800">{billing.summary?.total_events || 0}</div>
                    <div className="text-xs text-gray-500 uppercase mt-1">Total eventos</div>
                  </div>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-yellow-800">{billing.summary?.handoff_total || 0}</div>
                    <div className="text-xs text-yellow-600 uppercase mt-1">Handoff</div>
                    <div className="text-[10px] text-yellow-500">no facturable</div>
                  </div>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-center">
                    <div className="text-2xl font-bold text-gray-800">{billing.summary?.draft_total || 0}</div>
                    <div className="text-xs text-gray-500 uppercase mt-1">Borradores</div>
                    <div className="text-[10px] text-gray-400">no facturable</div>
                  </div>
                </div>
              </div>

              {billing.by_channel && (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                  {billing.by_channel.map(c => (
                    <div key={c.channel} className="bg-white rounded-lg shadow p-4 flex items-center gap-4">
                      <div className="text-2xl font-bold" style={{ color: c.channel === 'email' ? '#3b82f6' : c.channel === 'whatsapp' ? '#22c55e' : '#f97316' }}>
                        {c.channel === 'email' ? 'E' : c.channel === 'whatsapp' ? 'W' : 'C'}
                      </div>
                      <div>
                        <div className="text-xs text-gray-500 uppercase">{c.channel}</div>
                        <div className="text-xl font-bold text-gray-800">{c.billable} <span className="text-xs font-normal text-green-600">facturables</span></div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {billing.by_month && billing.by_month.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <h3 className="text-sm font-semibold text-gray-700">Facturacion por mes</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">Mes</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Facturables</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">XIA Auto</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Via Entradas</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Manual</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Total eventos</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">% Auto</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {billing.by_month.map(m => {
                        const pctAuto = m.billable > 0 ? ((m.xia_auto / m.billable) * 100).toFixed(0) : '-';
                        return (
                          <tr key={m.month} className="hover:bg-gray-50">
                            <td className="px-3 py-2 font-medium text-gray-800">{m.month}</td>
                            <td className="px-3 py-2 text-right font-bold text-green-700">{m.billable}</td>
                            <td className="px-3 py-2 text-right text-blue-600">{m.xia_auto}</td>
                            <td className="px-3 py-2 text-right text-cyan-600">{m.xia_from_entradas}</td>
                            <td className="px-3 py-2 text-right text-orange-600">{m.manual_human}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{m.total_events}</td>
                            <td className="px-3 py-2 text-right text-gray-500">{pctAuto}%</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {billing.by_day && billing.by_day.length > 0 && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Facturables por dia</h3>
                  <div className="space-y-1">
                    {(() => {
                      const maxB = Math.max(...billing.by_day.map(d => d.billable), 1);
                      return billing.by_day.map(d => (
                        <div key={d.date} className="flex items-center gap-2">
                          <span className="text-xs text-gray-500 w-16 shrink-0">
                            {new Date(d.date).toLocaleDateString('es-ES', { day: '2-digit', month: 'short' })}
                          </span>
                          <div className="flex-1 bg-gray-100 rounded-full h-4 overflow-hidden flex">
                            {d.xia_auto > 0 && (
                              <div className="h-full" title={'XIA Auto: ' + d.xia_auto}
                                style={{ width: Math.max(2, (d.xia_auto / maxB) * 100) + '%', background: '#3b82f6' }} />
                            )}
                            {d.xia_from_entradas > 0 && (
                              <div className="h-full" title={'Via Entradas: ' + d.xia_from_entradas}
                                style={{ width: Math.max(2, (d.xia_from_entradas / maxB) * 100) + '%', background: '#06b6d4' }} />
                            )}
                            {d.manual_human > 0 && (
                              <div className="h-full" title={'Manual: ' + d.manual_human}
                                style={{ width: Math.max(2, (d.manual_human / maxB) * 100) + '%', background: '#f97316' }} />
                            )}
                          </div>
                          <span className="text-xs font-bold text-green-700 w-8 text-right">{d.billable}</span>
                        </div>
                      ));
                    })()}
                  </div>
                  <div className="flex gap-4 mt-2 text-xs text-gray-400">
                    <span><span className="inline-block w-3 h-3 rounded" style={{background:'#3b82f6'}}></span> XIA Auto</span>
                    <span><span className="inline-block w-3 h-3 rounded" style={{background:'#06b6d4'}}></span> Via Entradas</span>
                    <span><span className="inline-block w-3 h-3 rounded" style={{background:'#f97316'}}></span> Manual</span>
                  </div>
                </div>
              )}

              {billing.by_intent && billing.by_intent.length > 0 && (
                <div className="bg-white rounded-lg shadow overflow-hidden">
                  <div className="px-4 py-3 bg-gray-50 border-b">
                    <h3 className="text-sm font-semibold text-gray-700">Facturables por intent</h3>
                  </div>
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs text-gray-500">Intent</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Facturables</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">XIA Auto</th>
                        <th className="px-3 py-2 text-right text-xs text-gray-500">Via Entradas</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {billing.by_intent.map(i => (
                        <tr key={i.intent} className="hover:bg-gray-50">
                          <td className="px-3 py-2 text-gray-800 truncate max-w-[200px]" title={i.intent}>{i.intent}</td>
                          <td className="px-3 py-2 text-right font-bold text-green-700">{i.billable}</td>
                          <td className="px-3 py-2 text-right text-blue-600">{i.xia_auto}</td>
                          <td className="px-3 py-2 text-right text-cyan-600">{i.xia_from_entradas}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {billing.by_sent_by && (
                <div className="bg-white rounded-lg shadow p-4">
                  <h3 className="text-sm font-semibold text-gray-700 mb-3">Por tipo de envio</h3>
                  <div className="grid grid-cols-3 gap-3">
                    {billing.by_sent_by.map(s => {
                      const colors = {
                        XIA_AUTO: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-800' },
                        XIA_FROM_ENTRADAS: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-800' },
                        MANUAL_HUMAN: { bg: 'bg-orange-50', border: 'border-orange-200', text: 'text-orange-800' },
                      };
                      const c = colors[s.sent_by] || { bg: 'bg-gray-50', border: 'border-gray-200', text: 'text-gray-800' };
                      return (
                        <div key={s.sent_by} className={`${c.bg} border ${c.border} rounded-lg p-3 text-center`}>
                          <div className={`text-2xl font-bold ${c.text}`}>{s.count}</div>
                          <div className="text-xs text-gray-600 mt-1">{s.label}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              <div className="bg-gray-50 rounded-lg p-4 text-xs text-gray-500">
                <strong>Reglas de facturacion:</strong> 1 unidad = 1 envio outbound al cliente (direction=outbound, recipient=cliente, status=sent).
                Cuenta: email_sent (XIA auto), learning_reply_sent (via Entradas), correction_sent (manual).
                No cuenta: handoff a Entradas, borradores no enviados, emails internos, tests.
              </div>
            </div>
          )}

          {/* By Topic + By Language */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-sm font-semibold text-gray-700">Top topics</h3>
              </div>
              <div className="overflow-auto max-h-80">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Topic</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">Total</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {topicsWithPct.slice(0, 20).map(t => (
                      <tr key={t.topic} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-800 truncate max-w-[200px]" title={t.topic}>{t.topic}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{t.count}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{t.pct}%</td>
                      </tr>
                    ))}
                    {topicsWithPct.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Sin datos</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="bg-white rounded-lg shadow overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b">
                <h3 className="text-sm font-semibold text-gray-700">Por idioma</h3>
              </div>
              <div className="overflow-auto max-h-80">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs text-gray-500">Idioma</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">Total</th>
                      <th className="px-3 py-2 text-right text-xs text-gray-500">%</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {langsWithPct.map(l => (
                      <tr key={l.language} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-800">
                          <span className="uppercase font-mono bg-gray-100 px-1.5 py-0.5 rounded text-xs mr-1">{l.language}</span>
                        </td>
                        <td className="px-3 py-2 text-right text-gray-600">{l.count}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{l.pct}%</td>
                      </tr>
                    ))}
                    {langsWithPct.length === 0 && (
                      <tr><td colSpan={3} className="px-3 py-4 text-center text-gray-400">Sin datos</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function KpiCard({ label, value, color = 'gray', subtitle }) {
  const colors = {
    blue: 'bg-blue-50 border-blue-200 text-blue-800',
    green: 'bg-green-50 border-green-200 text-green-800',
    purple: 'bg-purple-50 border-purple-200 text-purple-800',
    yellow: 'bg-yellow-50 border-yellow-200 text-yellow-800',
    gray: 'bg-gray-50 border-gray-200 text-gray-800',
  };
  return (
    <div className={`${colors[color]} border rounded-lg p-4 text-center`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs uppercase mt-1 opacity-70">{label}</div>
      {subtitle && <div className="text-[10px] mt-0.5 opacity-50">{subtitle}</div>}
    </div>
  );
}

export default AlcazarReporting;
